import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { allowedUploadExtensions } from '@app/core/constants';
import { ICoreAuthorizationDTO, ICoreMemberDTO, IDocumentResponseDTO } from '@app/core/model';
import { UploadFile } from '@app/core/model/upload-file.model';
import { NotificationService } from '@app/core/services';
import { DocumentsContextService, DocumentsReferencesContextService, DocumentsUploadContextService } from '@app/core/services/context/factory';
import { PlatformService } from '@app/core/services/context/platform.service';
import { UploadError } from '@app/shared/models';
import { ShellService } from '@mynexus/mfe-core';
import { FeatureToggleService, LineOfBusiness, ProductNames } from '@mynexus/mynexus-ui-lib';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { switchMap } from 'rxjs';
import { ContinuedStayRouterEvents } from '../models/continued-stay-router-events.model';
import { DateTimeModalComponent } from './date-time-modal/date-time-modal.component';

@Component({
    selector: 'mnc-documents-upload',
    templateUrl: './documents-upload.component.html',
    styleUrls: ['./documents-upload.component.scss'],
    standalone: false
})
export class DocumentsUploadComponent implements OnInit {
  @Input() authorizationByProductId: string
  @Input() authorizationNumber: string;
  @Input() isExternal: boolean;
  @Input() lineOfBusinessId: string;

  /* PAC */
  @Input() authorization: ICoreAuthorizationDTO;
  @Input() servicingProvider: any;
  @Input() documents: IDocumentResponseDTO[];
  @Input() lineData: object[];
  @Input() memberDto: ICoreMemberDTO;
  @Input() tenantName: string;
  @Input() tenantId: string;
  @Input() providerNPI: string;
  /* PAC */

  @Output() selectedFiles = new EventEmitter<UploadFile[]>();
  @Output() uploadComplete = new EventEmitter<UploadFile[]>();
  @Output() closeNote = new EventEmitter<void>();
  @Output() routerEvents = new EventEmitter<ContinuedStayRouterEvents>();

  form = new FormGroup({
    description: new FormControl('', Validators.maxLength(40)),
    docTypes: new FormControl([], Validators.required),
  });

  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly platformService = inject(PlatformService);
  private pacProductAndLobMigration = false;
  
  uploadFiles: UploadFile[] = [];
  isUploading = false;
  limitFour = false;
  fileTypeErrors = false;
  fileSizeErrors = false;
  fileNameSizeErrors = false;
  fileNameInvalidErrors = false;
  filesSizeSum = 0;
  documentTypes = [];

  allowedExtensions: string[] = allowedUploadExtensions;
  maxFileSize = 20 * 1024 * 1024; // 20Mb
  maxFileNameSize = 130;
  uploadMessage: { severity: string; summary: string; detail: string; } = null;
  /* PAC */
  forceContinuedStayRequest = false;
  selectionLimit = null;
  /* PAC */

  get selectedTypes(): AbstractControl {
    return this.form.get('docTypes');
  }

  get description(): AbstractControl {
    return this.form.get('description');
  }

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly dialogService: DialogService,
    private readonly notificationService: NotificationService,
    private readonly shellService: ShellService,
    private readonly documentsReferencesContextService: DocumentsReferencesContextService,
    private readonly documentsUploadContextService: DocumentsUploadContextService,
    private readonly documentsContextService: DocumentsContextService,
    private readonly contextService: PlatformService,
    private readonly confirmationService: ConfirmationService,
    private destroyRef: DestroyRef,
  ) { }

  ngOnInit(): void {
    this.featureToggleService.getFeatureAccess('PacProductAndLobMigration')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isEnabled) => {
        this.pacProductAndLobMigration = isEnabled;
      });

    this.documentsReferencesContextService.getDocumentReferences(this.lineOfBusinessId, 'Upload')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(documentTypes => {
          return this.documentsUploadContextService.manageForbiddenDocumentType(
            this.documents, this.documentsContextService,
            documentTypes, this.authorization?.authorizationStatus,
            this.authorization?.streamData?.['productName'],
            this.servicingProvider,
            this.contextService.widgetFeatures.createContinuedStayRequest
          )}),
      )
      .subscribe(res => {
        this.documentTypes = res.map(el => el.description);
      });
    this.getUploadMessage();

    this.setSelectionAbility = this.setSelectionAbility.bind(this);
  }

  getUploadMessage() {
    this.uploadMessage = this.documentsReferencesContextService.getUploadMessage(this.authorizationNumber, this.isExternal);
  }

  sumAllFileSize() {
    this.filesSizeSum = this.uploadFiles.reduce((sum, { fileMetaData }) => sum + (fileMetaData as any).size, 0);
  }

  // TODO
  onError(errors: UploadError[]) {
    this.fileTypeErrors = errors.some(e => e.type === 'accept');
    this.fileSizeErrors = errors.some(e => e.type === 'fileSize');
    this.fileNameSizeErrors = errors.some(e => e.type === 'fileName');
    this.fileNameInvalidErrors = errors.some(e => e.type === 'fileNameInvalid');
    this.fileTypeErrors ? this.notificationService.showError(
      'Please upload only .jpg, .jpeg, .png, .pdf files.',
      'File format is not valid',
    ) : null;
    this.fileSizeErrors ? this.notificationService.showError(
      'Maximum upload size is 20Mb.',
      'File size is invalid',
    ) : null;
    this.fileNameSizeErrors ? this.notificationService.showError(
      'The file name must not exceed ' + this.maxFileNameSize + ' characters. Rename the file and try again.',
      'File name is too long',
    ) : null;
    this.fileNameInvalidErrors ? this.notificationService.showError(
      'The file name contains invalid characters. Please rename the file and try again.',
      'Invalid File Name',
    ) : null;
    this.resetUploadFields();
  }

  resetValidationErrors() {
    this.fileTypeErrors = false;
    this.fileSizeErrors = false;
    this.fileNameSizeErrors = false;
    this.fileNameInvalidErrors = false;
  }

  // make sure no more than 4 files selected
  checkLimit() {
    this.limitFour = this.uploadFiles.length >= 4;
  }

  addFile(files: File[]) {
    if (this.uploadFiles.length < 4) {
      if (this.isExternal) {
        this.addSelected(files);
      } else {
        const ref = this.dialogService.open(DateTimeModalComponent, {
          header: 'Select the Date and Time Request Received',
          width: '500px',
          modal: true,
        });

        ref.onClose.subscribe((dateTimeFaxReceived: string) => {
          if (dateTimeFaxReceived) {
            this.addSelected(files, dateTimeFaxReceived);
          } else {
            this.resetUploadFields();
          }
          this.ref.markForCheck();
        });
      }
      this.ref.markForCheck();
    }
  }

  // add selected to temp list
  async addSelected(files: FileList | File[], dateTimeFaxReceived?: string) {
    const file: File = files[0];
    const indexNumber = this.uploadFiles.length < 1 ? 0 : this.uploadFiles.length;
    this.sumAllFileSize();

    if (this.filesSizeSum + file.size > this.maxFileSize) {
      // TODO
      const error: UploadError[] = [{ type: 'fileSize', name: file.name }];
      this.onError(error);
    } else {
      this.resetValidationErrors();

      try {
        const fileBase64: string = await this.readFileAsync(file) as string;
        const fileContent = fileBase64.split(',')[1];
        this.uploadFiles[indexNumber] = {
          fileMetaData: {
            size: file.size,
            type: file.type,
          },
          description: this.description.value,
          authorizationId: this.authorizationByProductId,
          authorizationNumber: this.authorizationNumber,
          tenantId: this.shellService.getTenantId(),
          documentSource: 'Upload',
          fileContent,
          documentTypes: this.selectedTypes.value,
          dateTimeFaxReceived: dateTimeFaxReceived || new Date().toUTCString(),
          documentFileName: this.addFilenameTimestamp(file.name),
          isAutomaticOCRDocument:false
        };
        //set document type is of OASis or not
        this.uploadFiles[indexNumber].isAutomaticOCRDocument=this.isDocTypeOASIS(indexNumber);
        // TO DO

        // hh only - 203971
        if (this.contextService.widgetFeatures.uploadDocsConfirmation) {
          this.uploadFiles[indexNumber].isNeedCheckStaff = await this.confirmNeedCheckStaff();
        }

        // This Warning should not Display For pac and pac commercial
        if (this.isExternal && this.contextService.widgetFeatures.reviewDocumentsMessage) {
          this.uploadFiles[indexNumber].isNeedCheckStaff = false;
          this.notificationService.showInfo('Uploaded documents will not be reviewed for authorizations that are no longer pending.', 'Document Review');
        }

        this.checkLimit();
        this.resetUploadFields();

      } catch (error) {
        this.notificationService.showError('Error while uploading file.');
      }
    }

    this.ref.markForCheck();
  }

  removeItem(index: number) {
    this.uploadFiles.splice(index, 1);
    this.resetUploadFields();
    this.checkLimit();
  }

  // Upload new document
  uploadDocuments() {
    if (this.uploadFiles.length > 0) {
      this.isUploading = true;
      const uploadFileDTOs: UploadFile[] = this.uploadFiles.map(f => {
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { fileMetaData, ...newFile } = f;
        return newFile;
      });

      this.onDocumentsUpload(uploadFileDTOs);

      this.filesSizeSum = 0;
      this.uploadFiles = [];
      this.limitFour = false;
      this.isUploading = false;
    }
  }
  /* HH */
  async confirmNeedCheckStaff(): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.confirmationService.confirm({
        accept: () => resolve(true),
        reject: () => resolve(false)
      });
    });
  }
  /* HH */

  /* PAC */

  public navigateToCreateContinuedStayPage(): void {
    const isCommercial = this.pacProductAndLobMigration
      ? this.authorization?.streamData['productName'] === ProductNames.PACIM && this.platformService?.lineOfBusiness === LineOfBusiness.Commercial
      : this.authorization?.streamData['productName'] === ProductNames.PacCommercial;
    const productName = isCommercial ? 'pac-commercial' : 'pac-im';

    this.routerEvents.emit({
      type: 'continued-stay',
      payload: {
        tenantId: this.shellService.getTenantId(),
        productName,
        authorizationNumber: this.authorizationNumber,
        nomncRescindSelected: this.forceContinuedStayRequest,
      }
    });
  }

  handleLevelAdjustmentUpload(): void {
    this.documentsUploadContextService.levelAdjustmentUpload(this.selectedTypes.value);
  }
  /* PAC */

  private addFilenameTimestamp(fileName: string): string {
    const ext = fileName.split('.').pop();
    const name = fileName.split('.').slice(0, -1).join('.');

    const date = new Date();
    const addLeadingZero = (num: number): string => {
      return String(num).padStart(2, '0');
    }
    // Gives timestamp in YYYYMMddHHmmss format (Could switch to pipe or date transform instead)
    const timestamp = `${date.getFullYear()}${addLeadingZero(date.getMonth() + 1)}${addLeadingZero(date.getDate())}`
      + `${addLeadingZero(date.getHours())}${addLeadingZero(date.getMinutes())}${addLeadingZero(date.getSeconds())}`;

    return `${name}_${timestamp}.${ext}`;
  }

  setSelectionAbility(data: string): boolean {
    return this.documentsUploadContextService.setSelectionAbility(data, this.selectedTypes);
  }

  docTypesSelected(event) {
    const selected = event?.value;

    this.forceContinuedStayRequest = this.documentsUploadContextService.forceContinuedStayRequest(selected, this.selectedTypes);

    if (this.forceContinuedStayRequest) {
      this.description?.disable();
      this.selectionLimit = 1;
    } else {
      this.description?.enable();
      this.selectionLimit = null;
    }
  }


  private readFileAsync(file: File): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private resetUploadFields() {
    this.form.reset();
  }

  isDocTypeOASIS(index: number) {
    const file: UploadFile = this.uploadFiles[index];
    const keyword = "OASIS";
    let isOasis = false;

    isOasis = file.documentFileName.toUpperCase().includes(keyword);
    if (!isOasis) {
      // checking whether description contains OASis
      if (file.description && file.description.trim() !== '' && file.description.toUpperCase().includes(keyword)) {
        isOasis = true;
      }
      else {
        // checking whether any of selected document types contains OASIS
        isOasis = file.documentTypes.some(b => b.toUpperCase().includes(keyword));
      }
    }
    return isOasis;
  }

  onDocumentsUpload(data: UploadFile[]): void {
    this.closeNote.emit();

    this.documentsContextService.uploadFile(this.tenantId, this.authorizationByProductId, data, true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.uploadComplete.emit();
      },
      error: () => this.notificationService.showError()
    });
  }
}
